<template>
    <q-card v-bind="attrs">
        <q-card-section>
            <div class="row"><div class="col-8 text-h6 underline text-uppercase">Communities at Sea</div><div class="col-4 text-caption print-hide">About these data <q-icon name="help_outline" class="q-pl-sm pointer" size="xs"   @click="infoDialogs['about'].model = !infoDialogs['about'].model"/></div></div>
        </q-card-section>
        <div class="q-pl-md q-pr-md text-caption text-left">Communities at Sea data makes it possible to see linkages between fishing ports and the fishing locations that are most important to them. The tool does not estimate the economic value of the communities shown, only its location and relative effort. Change the time period using the slider. </div>

        <q-card-section id="casPane" v-if="communityData['summaryNums'] && communityData['summaryNums']['comms'] && communityData['summaryNums']['comms']>0">
            <div class="row  time-slider-container q-mt-sm">
                <div class="col-3 text-body2 text-left q-pb-md">Select a time period:</div>
                <div class="col-8">
                    <q-slider
                        selection-color="transparent"
                        v-model="timePeriod"
                        color="secondary"
                        markers
                        label
                        :label-value=timeLabels[timePeriod]
                        label-always
                        :min="1"
                        :max="5"
                        @update:model-value="timeChange"
                        aria-label="Select time period"
                    />
                </div>
            </div>
            <div class="row justify-center items-center bg-grey-3 q-pa-md">

                <div class="col-2 text-body2 q-mr-md">In the AOI: {{ timeLabels[timePeriod] }}</div>
                <div class="col-3 text-body2 text-center q-ma-sm q-pa-md bg-white"><span v-if="communityData['summaryNums']" class="text-bold text-h5">{{ communityData['summaryNums']['comms'] }}</span> communities</div>
                <div class="col-5 q-ml-md">
                    <div class="text-h6">Using <span v-if="communityData['summaryNums']" class="text-bold">{{ communityData['summaryNums']['gears'] }}</span> gear types</div>
                    <div class="text-h6">from <span v-if="communityData['summaryNums']" class="text-bold">{{ communityData['summaryNums']['ports'] }}</span> ports</div>
                    <div class="text-h6">from <span v-if="communityData['summaryNums']" class="text-bold">{{ communityData['summaryNums']['states'] }} states</span></div>
                </div>
            </div>
            <div >
                <div class="row q-mt-lg text-h6">
                    <div class="col-11">
                        Fishing 
                        <span class="text-bold" v-if="dependencyIntensity">Dependency: </span>
                        <span class="text-bold" v-else>Intensity: </span> 
                        <span v-if="dependencyIntensity">% fisher days in AOI</span>
                        <span v-else># fisher days</span>
                        <q-btn v-if="dependencyIntensity" dense size="xs" flat class="gray q-mb-md" icon="help_outline" @click="infoDialogs['dependency'].model = !infoDialogs['dependency'].model" aria-label="Fishing dependency help"/>
                        <q-btn v-else dense size="xs" flat class="gray q-mb-md" icon="help_outline" @click="infoDialogs['intensity'].model = !infoDialogs['intensity'].model" aria-label="Fishing  intensity help"/>
                    </div>
                    <div class="col-1">
                        <q-toggle v-model="dependencyIntensity" size="sm" aria-label="Choose fishing dependency or fishing intensity"/>
                    </div>
                </div>
                <div class="q-pb-sm text-caption text-left">Below is a list of ports that harbor at least one fleet of vessels (grouped by gear type) whose fishing activity overlaps with the area of interest.  Click on a bubble to see the map. Switch the toggle to change from Fishing Dependency to Fishing Intensity. Note that because of confidentiality data issues, some communities may be missing from this section. See data considerations for more details.</div>
                <div class="table-container">
                    <Table 
                        v-if="communityData['dependencyData']"
                        :columns="columns" 
                        :rows="dependencyIntensity ? communityData['dependencyData'] : communityData['intensityData']" 
                        :version="version"
                        :colorFunc="dependencyColorFunc"
                        :labelColorFunc="labelColorFunc"
                        :mapStyle="true"
                        :mapLayer="styleLayer"
                        :manualMapLegend="casMapLegend"
                        :mapStopFunc="mapStopFunc"
                        :includeFilter="false"
                        :additionalClickFunc="highlightPort"
                        :selCell="selectedStyleAttr"
                        mapStyleType="step"
                    />
                    <div class="row" >
                        <div class="col-6">
                            <div v-for="name, code, i in gearCodesPretty">
                                <div v-if="i<=5">{{ code}} : {{ name}}</div>
                            </div>
                        </div>
                        <div class="col-6">
                            <div v-for="name, code, i in gearCodesPretty">
                                <div v-if="i>5">{{ code}} : {{ name}}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </q-card-section>
        <q-card-section class="" v-else>
            <div class="row justify-center">
                No Communities at Sea data is available within the area of interest.
            </div>
        </q-card-section>
    </q-card>
    <div v-for="(infoObj, name) in infoDialogs" :key="name" >
        <q-dialog v-model="infoObj.model">
            <InfoDialog  :html="infoObj.text" :name="infoObj.pName"/>
        </q-dialog>
    </div>
</template>
<script setup lang="ts">
import { toRefs, computed, watch, onMounted, onUnmounted, ref, useAttrs } from "vue";
import { useHelpers } from "../composables/useHelpers"
import { useMapItems } from "../composables/useMapItems"
import { QTableColumn } from 'quasar';
import InfoDialog from "./InfoDialog.vue";
import Table from "./Table.vue";

const {  ecos, map, tileLayers, toggleLayer, mapStyleInfo, clearMapStyle, activeLayerID, processedData, setProcessedData, loadDataByID, styleMap, setLegend } = useMapItems()
const { arrayFindObjectByProp,  getKeyByValue,  isFloat } = useHelpers();
const attrs = useAttrs();

const props = defineProps({
    rawData: { type: Object },
    version: { type: Number },
})

const { rawData, version } = toRefs(props)

const communityData = computed(() =>{
    const d = processCommunityData(casLayer.value, version)
    // console.log(d)
    return d
})

const  processCommunityData =(casLayerID, version) =>{
    // console.log(casLayerID)
    // console.log(rawData.value)

    years.value = casLayerID.substring(4, 13)
    // console.log(years.value)
    if (
        !processedData.value[version.value] || 
        processedData.value[version.value][casLayerID] === undefined ||
        processedData.value[version.value][casLayerID]["summaryNums"]=== undefined ||
        processedData.value[version.value][casLayerID]["summaryNums"]["a"]=== undefined
    ){  
        
        // console.log(rawData.value[casLayerID])
        const cellData = rawData.value[casLayerID];
        if (cellData != undefined && cellData.length >0){
            // console.log(cellData)
            let portData = {}
            let percCellsPortsByGear = {} //what % of the AOI is used by fishers from each port for each gear
            let numCommunities = 0
            let numGears = 0
            let numPorts = 0
            let numStates = 0
            let summaryNums = {}
            let dependencyData = []
            let intensityData = []
            const gearTypes = Object.keys(gearColors)

            // % of AOI by gear type data structure
            gearTypes.forEach((gear, i) => {
                percCellsPortsByGear[gear] = {}
            });

            // create empty structure by communtiy
            for (const [pport, gears] of Object.entries(allGearsByPort)) {
                portData[pport] = {}
                gears.forEach((gear, i) => {
                    portData[pport][gear] = {}
                    portData[pport][gear][years.value] = []
                })
            }

            // console.log(portData)

            // loop through each cell and add the ports to the data array
            for (var i = 0; i < cellData.length; i++) {
                for (const [key, value] of Object.entries(cellData[i])) {
                    //main data
                    const excludeFields = ["OBJECTID", "OBJECTID_1", "ECOREG", "AREA_SQK", "GRID_ID", "ID", "Id", "Shape", "Shape_Length", "Shape_Area"]
                    if (!excludeFields.includes(key)){
                        const port = key.substring(0, 4)
                        const pport = portPrettyNames[port]
                        const gear = gearCodes[key.substring(4, 8)]
                        
                        if (portData[portPrettyNames[port]][gear]){
                            //will be summing the fisher days values, so exclude -9
                            if (value !== -9){ 
                                portData[pport][gear][years.value].push(value)
                            }
                        }
                    }
                }
            }

            //build the final data objects and add to arrays
            for (const [pport, gearObj] of Object.entries(portData)) {
                // console.log(gearObj)
                const dependencyObj = {
                    name: pport,
                }
                const intensityObj = {
                    name: pport,
                }
                for (const [gear, yearObj] of Object.entries(gearObj)) {
                    const p = getKeyByValue(portPrettyNames, pport)
                    const g = gearCodesReverse[gear]
                    const comm = p + g
    					
                    // if communityFisherDaysTotal for the community is 0, can't divide by 0, so calculate spearately
					let dependencyVal
                    if (communityFisherDaysTotal[comm + years.value] >0){
                        dependencyVal = (yearObj[years.value].reduce((a, b) => a + b, 0)) / communityFisherDaysTotal[comm + years.value] * 100
                    }
                    else{
                        dependencyVal = 0
                    }
                    dependencyObj[gear]= Math.round(dependencyVal *10)/10;
                    intensityObj[gear]= Math.round((yearObj[years.value].reduce((a, b) => a + b, 0)));
                    
                    // loop through all gear types.  If its not in the objects, add it with a 0
                    gearTypes.forEach((gear, i) => {
                        if (!Object.keys(dependencyObj).includes(gear)){
                            dependencyObj[gear] =0
                            intensityObj[gear] =0
                        }
                    })
                }
                
                // only push the data objects into the final array if there's a non-zero value
                let sum = 0
                Object.values(dependencyObj).forEach((val) =>{
                    if (isFloat(val)){
                        sum += parseFloat(val as string)
                    }
                })
                if(sum >0){
                    dependencyData.push(dependencyObj)
                    intensityData.push(intensityObj)
                }
            }

            // get the annual summary numbers for current time.   Get it from already calculated time data
            const uniqueGears = []
            const uniquePorts = []
            const uniqueStates = []
            const portCodes = []
            dependencyData.forEach((portObj) =>{
                const p = getKeyByValue(portPrettyNames, portObj.name)
                uniquePorts.push(portObj.name)
                portCodes.push(p)
                uniqueStates.push(p.substring(0, 2))
                for (const [gear, val] of Object.entries(portObj)) {
                    if (gear != 'name' && (val as number) > 0){
                        numCommunities += 1
                        uniqueGears.push(gear)
                    }
                }
            })

            // filter the ports that have a community in the AOI and add to the map
            const unqiuePortCodes = new Set(portCodes)
            const portFilter =  ["in", 'TOWNCODE', ...unqiuePortCodes];
            map.value.setFilter('crcs-ne-cas-ports', portFilter);
            map.value.setLayoutProperty('crcs-ne-cas-ports', 'visibility', 'visible');

            //get summary numbers
            numGears = Array.from(new Set(uniqueGears)).length
            numPorts = Array.from(new Set(uniquePorts)).length
            numStates = Array.from(new Set(uniqueStates)).length
            summaryNums["comms"] = numCommunities
            summaryNums["gears"] = numGears
            summaryNums["ports"] = numPorts
            summaryNums["states"] = numStates

            // console.log(summaryNums)
            // console.log(dependencyData)
            // console.log(intensityData)
            setProcessedData(version.value, casLayerID, "summaryNums", "a", summaryNums)
            setProcessedData(version.value, casLayerID, "dependencyData", "a", dependencyData)
            setProcessedData(version.value, casLayerID, "intensityData", "a", intensityData)
            setProcessedData(version.value, casLayerID, "portFilter", "a", portFilter)


            let returnObj = {}
            if (numCommunities > 0){
                returnObj['summaryNums'] = summaryNums;
                returnObj['dependencyData'] = dependencyData;
                returnObj['intensityData'] = intensityData;
            }
            return returnObj
        }
        else{return {}}
    }

    else{
        // console.log("loading saved CAS results")
        // console.log(processedData.value)
        const summaryNums = processedData.value[version.value][casLayerID]['summaryNums']['a']
        const dependencyData = processedData.value[version.value][casLayerID]['dependencyData']['a']
        const intensityData = processedData.value[version.value][casLayerID]['intensityData']['a']
        const portFilter = processedData.value[version.value][casLayerID]['portFilter']['a']
        let returnObj = {}
        returnObj['summaryNums'] = summaryNums;
        returnObj['dependencyData'] = dependencyData;
        returnObj['intensityData'] = intensityData;
        return returnObj
    }
}

const selectedPortCode = ref(null)
const selectedStyleAttr = ref(null)
const selectedLegendText = ref(null)

const applySelectedPortHighlight = () => {
    if (!map.value || !map.value.getLayer('highlighted-port')) return
    const portFilter = selectedPortCode.value ? ["==", 'TOWNCODE', selectedPortCode.value] : ["==", 'TOWNCODE', 'xx']
    map.value.setFilter('highlighted-port', portFilter)
}

const applySelectedStyle = () => {
    if (!selectedStyleAttr.value) return
    if (!map.value) return

    const layerID = casLayer.value
    const theseMapStops = mapStopFunc(selectedStyleAttr.value)

    const apply = () => {
        if (!map.value.getLayer(layerID)) return
        styleMap(layerID, selectedStyleAttr.value, 'step', theseMapStops, null)
        if (selectedLegendText.value){
            setLegend(selectedLegendText.value, casMapLegend)
        }
    }

    // Apply immediately if possible
    apply()

    // Also apply once the map has finished rendering the new layer (fallback)
    map.value.once('idle', apply)
}

const timeChange = () =>{
    const layer = arrayFindObjectByProp(tileLayers.value, 'id', casLayer.value)
    toggleLayer(true, layer, version)

    // Re-apply the selected port and styling once the layer is fully loaded
    if (map.value){
      map.value.once('idle', () => {
        applySelectedPortHighlight()
        applySelectedStyle()
      })
    }
}
const years = ref(null);
const timePeriods = {
    1 : "1996_2000",
    2 : "2001_2005",
    3: "2006_2010",
    4: "2011_2015",
    5: "2016_2020"
}
const timePeriod = ref(5)
const timeLabels = {
    1 : "1996-2000",
    2 : "2001-2005",
    3: "2006-2010",
    4: "2011-2015",
    5: "2016-2020"
}
const casLayer = computed(() =>{
    const layer = "CAS_" + timePeriods[timePeriod.value]
    // console.log(layer)
    return layer
})

watch(casLayer, () => {
    // Re-apply the selected port highlight and style when the CAS layer changes
    applySelectedPortHighlight()
    applySelectedStyle()
})

const mapStopFunc = (comm) =>{
    const thresholds = communityMapThresholds;

    // if there's no community give it a 999... 
    // then excluded from table & no map styling
    let thresholdVal
    if (thresholds[comm]){thresholdVal = thresholds[comm]}
    else{thresholdVal = 999}
    const stops = []
    stops.push([0, "#a1a1a1"])
    stops.push([0.000000001, "#F7E8F2"])
    stops.push(thresholdVal, "#830C5D")
    // console.log(stops)
    return stops
}


const styleLayer = computed(()=>{
    return "CAS_" + years.value 
})


const casMapLegend = [
    ["Community Use Area", "#F7E8F2"], 
    ["High Intensity Area (75th percentile)", "#830C5D"]
];

const gearColors ={
    "Groundfish65minus" : "#000000", //"#7779f7",
    "Groundfish65plus" : "#000000", //"#6e6324",
    "Longline" : "#000000", //"#007527",
    "Dredge" : "#000000", //"#f5950f",
    "Lobster" : "#000000", //"#93272C",
    "Gillnet" : "#000000", //"#fa2de5",
    "ShrimpTrawl" : "#000000", //"#c969c0",
    "OtherPotsTraps" : "#000000", //"#F6BE00",
    "MidwaterSeine" : "#000000", //"#77b7f7",
};
const gearCodes ={
    "GFMI" : "Groundfish65minus",
    "GFPL" : "Groundfish65plus",
    "GRMI" : "Groundfish65minus",
    "GRPL" : "Groundfish65plus",
    "LONG" : "Longline",
    "DRED" : "Dredge",
    "LOBS" : "Lobster",
    "GILL" : "Gillnet",
    "SHRM" : "ShrimpTrawl",
    "OPTR" : "OtherPotsTraps",
    "MSEI" : "MidwaterSeine"
};

const gearCodesPretty ={
    "GFMI" : "Groundfish <65'",
    "GFPL" : "Groundfish >65'",
    "LONG" : "Longline",
    "DRED" : "Dredge",
    "LOBS" : "Lobster Pots",
    "GILL" : "Gillnet",
    "SHRM" : "Shrimp Trawl",
    "OPTR" : "Other Pots & Traps",
    "MSEI" : "Midwater Seine"
};

const gearCodesReverse ={
    "Groundfish65minus" : "GFMI",  
    "Groundfish65plus" : "GFPL",  
    "Longline" : "LONG",  
    "Dredge" : "DRED",  
    "Lobster" : "LOBS",  
    "Gillnet" : "GILL",  
    "ShrimpTrawl" : "SHRM",  
    "OtherPotsTraps" : "OPTR",
    "MidwaterSeine" : "MSEI"  
};
const columns: QTableColumn[] = [
    {label: '', field:'name', name:'name', align:'left',  style: 'width:50px; max-width:85px; text-wrap:pretty;'},
    {label:"GFMI", field:"Groundfish65minus", name:"Groundfish65minus", align: 'center',sortable: false,  style: 'width:45px; max-width: 45px; text-wrap:pretty;' },
    {label:"GFPL", field:"Groundfish65plus",  name:"Groundfish65plus", align: 'center',sortable: false,  style: 'width:45px; max-width: 45px; text-wrap:pretty;'},
    {label:"LONG",field:"Longline", name:"Longline", align: 'center', sortable: false,  style: 'width:45px; max-width: 48px; text-wrap:pretty;'},
    {label:"DRED",field:"Dredge", name:"Dredge", align: 'center', sortable: false,  style: 'width:45px; max-width: 45px; text-wrap:pretty;'},
    {label:"LOBS", field:"Lobster", name:"Lobster", align: 'center', sortable: false,  style: 'width:45px; max-width: 45px; text-wrap:pretty;'},
    {label:"GILL", field:"Gillnet", name:"Gillnet", align: 'center', sortable: false,  style: 'width:45px; max-width: 45px; text-wrap:pretty;'},
    {label:"SHRM", field:"ShrimpTrawl", name:"ShrimpTrawl", align: 'center', sortable: false,  style: 'width:45px; max-width: 45px; text-wrap:pretty;'},
    {label:"OPTR", field:"OtherPotsTraps", name:"OtherPotsTraps", align: 'center',  sortable: false,  style: 'width:45px; max-width: 45px; text-wrap:pretty;'},
    {label:"MSEI", field:"MidwaterSeine", name:"MidwaterSeine", align: 'center', sortable: false,  style: 'width:45px; max-width: 45px; text-wrap:pretty;'},
];




const portPrettyNames = {
    "CTST" : "Stonington,  CT",
    "MABS" : "Boston,  MA",
    "MABV" : "Beverly,  MA",
    "MACH" : "Chatham,  MA",
    "MADN" : "Dennis,  MA",
    "MAFH" : "Fairhaven,  MA",
    "MAGL" : "Gloucester,  MA",
    "MAHL" : "Hull,  MA",
    "MAHP" : "Harwichport,  MA",
    "MAMF" : "Marshfield,  MA",
    "MAMH" : "Marblehead,  MA",
    "MANB" : "New Bedford,  MA",
    "MANP" : "Newburyport,  MA",
    "MAPL" : "Plymouth,  MA",
    "MAPT" : "Provincetown,  MA",
    "MARP" : "Rockport,  MA",
    "MASC" : "Scituate,  MA",
    "MASW" : "Sandwich,  MA",
    "MAWP" : "Westport,  MA",
    "MDOC" : "Ocean City,  MD",
    "MEBA" : "Bailey Island,  ME",
    "MEBE" : "Beals Island,  ME",
    "MEBO" : "Boothbay Harbor,  ME",
    "MECH" : "Cundys Harbor,  ME",
    "MECP" : "Cape Porpoise,  ME",
    "MEHW" : "Harpswell,  ME",
    "MEJP" : "Jonesport,  ME",
    "MEKB" : "Kennebunkport,  ME",
    "MEKT" : "Kittery,  ME",
    "MEPO" : "Portland,  ME",
    "MERK" : "Rockland,  ME",
    "MESB" : "Stueben,  ME",
    "MESG" : "Stonington,  ME",
    "MESH" : "Sprucehead,  ME",
    "METH" : "Tenants Harbor,  ME",
    "MEVH" : "Vinalhaven,  ME",
    "MEWH" : "Winter Harbor,  ME",
    "MEYH" : "York Harbor,  ME",
    "NCBE" : "Beaufort,  NC",
    "NCEN" : "Engelhard,  NC",
    "NCHA" : "Hatteras,  NC",
    "NCOR" : "Oriental,  NC",
    "NCWC" : "Wanchese,  NC",
    "NHNW" : "Newington,  NH",
    "NHPM" : "Portsmouth,  NH",
    "NHRY" : "Rye,  NH",
    "NHSB" : "Seabrook,  NH",
    "NJAT" : "Atlantic City,  NJ",
    "NJBF" : "Belford,  NJ",
    "NJBG" : "Barnegat,  NJ",
    "NJCM" : "Cape May,  NJ",
    "NJPP" : "Point Pleasant,  NJ",
    "NJSC" : "Sea Isle City,  NJ",
    "NJSR" : "Shark RIver,  NJ",
    "NJWW" : "Wildwood,  NJ",
    "NYFR" : "Freeport,  NY",
    "NYMO" : "Montauk,  NY",
    "NYPL" : "Point Lookout,  NY",
    "NYSC" : "Shinnecock,  NY",
    "RILC" : "Little Compton,  RI",
    "RINP" : "Newport,  RI",
    "RIPJ" : "Point Judith,  RI",
    "RITI" : "Tiverton,  RI",
    "VACH" : "Chincoteague,  VA",
    "VACS" : "City Of Seaford,  VA",
    "VAHA" : "Hampton,  VA",
    "VANN" : "Newport News,  VA",
    "VAVB" : "Virginia Beach,  VA"
}

const allGearsByPort ={
    'Stonington,  CT': ['Dredge', 'Groundfish65plus', 'Groundfish65minus'],
    'Boston,  MA': ['Lobster', 'Groundfish65plus', 'Groundfish65minus'],
    'Beverly,  MA': ['Lobster'],
    'Chatham,  MA': ['Gillnet', 'Lobster', 'Longline', 'Groundfish65minus'],
    'Dennis,  MA': ['Lobster'],
    'Fairhaven,  MA': ['Lobster', 'Dredge'],
    'Gloucester,  MA': ['OtherPotsTraps', 'Groundfish65minus', 'Lobster', 'Dredge', 'Gillnet', 'Groundfish65plus', 'Longline', 'MidwaterSeine'],
    'Hull,  MA': ['Lobster'],
    'Harwichport,  MA': ['Lobster', 'OtherPotsTraps'],
    'Marshfield,  MA': ['Lobster'],
    'Marblehead,  MA': ['Lobster'],
    'New Bedford,  MA': ['Groundfish65minus', 'Lobster', 'Dredge', 'Gillnet', 'Groundfish65plus', 'MidwaterSeine'],
    'Newburyport,  MA': ['Dredge'],
    'Plymouth,  MA': ['Lobster'],
    'Provincetown,  MA': ['Lobster', 'Dredge', 'Groundfish65minus'],
    'Rockport,  MA': ['Lobster'],
    'Scituate,  MA': ['Gillnet', 'Lobster', 'Groundfish65minus'],
    'Sandwich,  MA': ['Lobster'],
    'Westport,  MA': ['Lobster', 'Gillnet'],
    'Ocean City,  MD': ['Gillnet', 'Lobster', 'OtherPotsTraps', 'Groundfish65minus'],
    'Bailey Island,  ME': ['Lobster'],
    'Beals Island,  ME': ['Lobster'],
    'Boothbay Harbor,  ME': ['Lobster'],
    'Cundys Harbor,  ME': ['Lobster'],
    'Cape Porpoise,  ME': ['Lobster'],
    'Harpswell,  ME': ['Lobster'],
    'Jonesport,  ME': ['Lobster', 'Dredge'],
    'Kennebunkport,  ME': ['Lobster'],
    'Kittery,  ME': ['Lobster'],
    'Portland,  ME': ['Groundfish65minus', 'Lobster', 'Dredge', 'Gillnet', 'Groundfish65plus', 'MidwaterSeine'],
    'Rockland,  ME': ['MidwaterSeine'],
    'Stueben,  ME': ['Lobster'],
    'Stonington,  ME': ['Lobster'],
    'Sprucehead,  ME': ['Lobster'],
    'Tenants Harbor,  ME': ['Lobster'],
    'Vinalhaven,  ME': ['Lobster'],
    'Winter Harbor,  ME': ['Lobster'],
    'York Harbor,  ME': ['Lobster'],
    'Beaufort,  NC': ['ShrimpTrawl', 'Groundfish65plus'],
    'Engelhard,  NC': ['ShrimpTrawl', 'Groundfish65plus', 'Gillnet'],
    'Hatteras,  NC': ['Gillnet'],
    'Oriental,  NC': ['ShrimpTrawl', 'Groundfish65plus'],
    'Wanchese,  NC': ['ShrimpTrawl', 'Groundfish65plus', 'Gillnet'],
    'Newington,  NH': ['Lobster'],
    'Portsmouth,  NH': ['Gillnet', 'Lobster', 'Groundfish65minus'],
    'Rye,  NH': ['Gillnet'],
    'Seabrook,  NH': ['Lobster', 'Groundfish65minus'],
    'Atlantic City,  NJ': ['OtherPotsTraps'],
    'Belford,  NJ': ['Dredge', 'MidwaterSeine', 'Groundfish65plus', 'Groundfish65minus'],
    'Barnegat,  NJ': ['Dredge', 'Gillnet'],
    'Cape May,  NJ': ['OtherPotsTraps', 'Dredge', 'Gillnet', 'Groundfish65plus', 'MidwaterSeine'],
    'Point Pleasant,  NJ': ['OtherPotsTraps', 'Groundfish65minus', 'Lobster', 'Dredge', 'Gillnet', 'Groundfish65plus'],
    'Sea Isle City,  NJ': ['Lobster', 'OtherPotsTraps'],
    'Shark RIver,  NJ': ['Lobster'],
    'Wildwood,  NJ': ['Dredge'],
    'Freeport,  NY': ['Gillnet', 'Groundfish65minus'],
    'Montauk,  NY': ['OtherPotsTraps', 'Groundfish65minus', 'Lobster', 'Gillnet', 'Groundfish65plus'],
    'Point Lookout,  NY': ['Groundfish65plus'],
    'Shinnecock,  NY': ['Gillnet', 'Groundfish65minus'],
    'Little Compton,  RI': ['Lobster', 'OtherPotsTraps', 'Gillnet'],
    'Newport,  RI': ['Gillnet', 'Lobster', 'Groundfish65minus'],
    'Point Judith,  RI': ['OtherPotsTraps', 'Groundfish65minus', 'Lobster', 'Dredge', 'Gillnet', 'Groundfish65plus'],
    'Tiverton,  RI': ['Gillnet'],
    'Chincoteague,  VA': ['Gillnet', 'Groundfish65plus', 'OtherPotsTraps', 'Groundfish65minus'],
    'City Of Seaford,  VA': ['Dredge'],
    'Hampton,  VA': ['Dredge', 'Groundfish65plus'],
    'Newport News,  VA': ['Dredge', 'Groundfish65plus'],
    'Virginia Beach,  VA': ['OtherPotsTraps', 'Gillnet'] 
};

const communityFisherDaysTotal = {
    'CTSTDRED1996_2000': 17406.35527434945,
    'CTSTGFMI1996_2000': 1260.3950093388557,
    'CTSTGFPL1996_2000': 9510.805085331202,
    'MABSGFMI1996_2000': 2385.198529034853,
    'MABSGFPL1996_2000': 28414.498284310102,
    'MABSLOBS1996_2000': 1087.8416875302792,
    'MABVLOBS1996_2000': 9944.90415096283,
    'MACHGFMI1996_2000': 1783.6405356228352,
    'MACHGILL1996_2000': 17464.01450869441,
    'MACHLOBS1996_2000': 2223.5525094270706,
    'MACHLONG1996_2000': 6314.542468577623,
    'MADNLOBS1996_2000': 131.42734134197235,
    'MAFHDRED1996_2000': 2623.414660990238,
    'MAFHLOBS1996_2000': 6856.574552893639,
    'MAGLDRED1996_2000': 1529.3463744223118,
    'MAGLGFMI1996_2000': 26931.18859705329,
    'MAGLGFPL1996_2000': 33095.688193410635,
    'MAGLGILL1996_2000': 35841.983429551125,
    'MAGLLOBS1996_2000': 37352.93088877201,
    'MAGLLONG1996_2000': 5263.588403254747,
    'MAGLMSEI1996_2000': 4086.329053670168,
    'MAGLOPTR1996_2000': 6778.77976372838,
    'MAHLLOBS1996_2000': 2493.8673346936703,
    'MAHPLOBS1996_2000': 13.622818022966385,
    'MAHPOPTR1996_2000': 81.91511702537537,
    'MAMFLOBS1996_2000': 5650.840387970209,
    'MAMHLOBS1996_2000': 3168.361303269863,
    'MANBDRED1996_2000': 314444.4622595906,
    'MANBGFMI1996_2000': 9020.846177250147,
    'MANBGFPL1996_2000': 193954.2342468202,
    'MANBGILL1996_2000': 2167.2314420491457,
    'MANBLOBS1996_2000': 5326.759095847607,
    'MANBMSEI1996_2000': 0,
    'MANPDRED1996_2000': 93.28619310259819,
    'MAPLLOBS1996_2000': 2771.9244123399258,
    'MAPTDRED1996_2000': 1152.453930735588,
    'MAPTGFMI1996_2000': 10628.553246706724,
    'MAPTLOBS1996_2000': 465.2482967078686,
    'MARPLOBS1996_2000': 6941.978603512049,
    'MASCGFMI1996_2000': 2804.5281853675842,
    'MASCGILL1996_2000': 14497.625796556473,
    'MASCLOBS1996_2000': 3484.6382988989353,
    'MASWLOBS1996_2000': 9797.853061169386,
    'MAWPGILL1996_2000': 3220.311416476965,
    'MAWPLOBS1996_2000': 10076.32950425148,
    'MDOCGFMI1996_2000': 2824.824066698551,
    'MDOCGILL1996_2000': 1942.4833744466305,
    'MDOCLOBS1996_2000': 163.58876413106918,
    'MDOCOPTR1996_2000': 3424.7981908023357,
    'MEBALOBS1996_2000': 72.35005727410316,
    'MEBELOBS1996_2000': 518.4929938316345,
    'MEBOLOBS1996_2000': 2353.827082782984,
    'MECHLOBS1996_2000': 1007.5373590886593,
    'MECPLOBS1996_2000': 2352.3436640799046,
    'MEHWLOBS1996_2000': 1160.711748033762,
    'MEJPDRED1996_2000': 853.2899200320244,
    'MEJPLOBS1996_2000': 1680.1939362287521,
    'MEKBLOBS1996_2000': 0,
    'MEKTLOBS1996_2000': 1832.0917440354824,
    'MEPODRED1996_2000': 12.627033025026321,
    'MEPOGFMI1996_2000': 21072.15360581875,
    'MEPOGFPL1996_2000': 37584.408983528614,
    'MEPOGILL1996_2000': 13107.644328713417,
    'MEPOLOBS1996_2000': 5190.708850860596,
    'MEPOMSEI1996_2000': 2511.3406397402287,
    'MERKMSEI1996_2000': 2055.9457889944315,
    'MESBLOBS1996_2000': 519.0037634372711,
    'MESGLOBS1996_2000': 3289.266047477722,
    'MESHLOBS1996_2000': 1194.2022783458233,
    'METHLOBS1996_2000': 360.00530898571014,
    'MEVHLOBS1996_2000': 827.8062614798546,
    'MEWHLOBS1996_2000': 368.0008865594864,
    'MEYHLOBS1996_2000': 420.8704153597355,
    'NCBEGFPL1996_2000': 3267.3925673663616,
    'NCBESHRM1996_2000': 4025.2505433261395,
    'NCENGFPL1996_2000': 1046.0684930086136,
    'NCENGILL1996_2000': 372.91241389513016,
    'NCENSHRM1996_2000': 11.514303028583527,
    'NCHAGILL1996_2000': 120.57237929105759,
    'NCORGFPL1996_2000': 1389.6254960596561,
    'NCORSHRM1996_2000': 471.3549220561981,
    'NCWCGFPL1996_2000': 4541.938112467527,
    'NCWCGILL1996_2000': 480.8983418941498,
    'NCWCSHRM1996_2000': 276.33992543816566,
    'NHNWLOBS1996_2000': 18242.959309726954,
    'NHPMGFMI1996_2000': 2203.2176297605038,
    'NHPMGILL1996_2000': 13194.140632897615,
    'NHPMLOBS1996_2000': 11614.266540050507,
    'NHRYGILL1996_2000': 2499.615273654461,
    'NHSBGFMI1996_2000': 4520.487476706505,
    'NHSBLOBS1996_2000': 124.70725890994072,
    'NJATOPTR1996_2000': 901.9404073953629,
    'NJBFDRED1996_2000': 135.60341143608093,
    'NJBFGFMI1996_2000': 5053.168869435787,
    'NJBFMSEI1996_2000': 0.7002080082893372,
    'NJBGDRED1996_2000': 121.78942489624023,
    'NJBGGILL1996_2000': 482.3420528769493,
    'NJCMDRED1996_2000': 44553.34447297454,
    'NJCMGFPL1996_2000': 55444.77843615413,
    'NJCMGILL1996_2000': 55.96572595834732,
    'NJCMMSEI1996_2000': 5050.947304815054,
    'NJCMOPTR1996_2000': 1471.7008121013641,
    'NJPPDRED1996_2000': 17910.315282911062,
    'NJPPGFMI1996_2000': 6774.502571731806,
    'NJPPGFPL1996_2000': 9693.307080507278,
    'NJPPGILL1996_2000': 3872.0974009931087,
    'NJPPLOBS1996_2000': 2551.155401468277,
    'NJPPOPTR1996_2000': 95.45530584454536,
    'NJSCLOBS1996_2000': 206.4735718369484,
    'NJSCOPTR1996_2000': 1527.3677946925163,
    'NJSRLOBS1996_2000': 1667.2576043009758,
    'NJWWDRED1996_2000': 23.416978001594543,
    'NYFRGFMI1996_2000': 590.0239996910095,
    'NYFRGILL1996_2000': 19.163138031959534,
    'NYMOGFMI1996_2000': 2686.3093072474003,
    'NYMOGFPL1996_2000': 5798.800939410925,
    'NYMOGILL1996_2000': 112.02267023921013,
    'NYMOLOBS1996_2000': 2310.8492906987667,
    'NYMOOPTR1996_2000': 125.89161291718483,
    'NYPLGFPL1996_2000': 1421.1976333260536,
    'NYSCGFMI1996_2000': 14639.767954319715,
    'NYSCGILL1996_2000': 2670.713618516922,
    'RILCGILL1996_2000': 3568.8370339870453,
    'RILCLOBS1996_2000': 4367.528484284878,
    'RILCOPTR1996_2000': 900.0480356812477,
    'RINPGFMI1996_2000': 924.1829037070274,
    'RINPGILL1996_2000': 878.8352376818657,
    'RINPLOBS1996_2000': 16566.239746302366,
    'RIPJDRED1996_2000': 208.61698772013187,
    'RIPJGFMI1996_2000': 13312.833570420742,
    'RIPJGFPL1996_2000': 85050.17481634021,
    'RIPJGILL1996_2000': 1997.0320343077183,
    'RIPJLOBS1996_2000': 46635.72792071104,
    'RIPJOPTR1996_2000': 909.4710198938847,
    'RITIGILL1996_2000': 1523.393108189106,
    'VACHGFMI1996_2000': 46.116023033857346,
    'VACHGFPL1996_2000': 234.7607357800007,
    'VACHGILL1996_2000': 1440.1317525207996,
    'VACHOPTR1996_2000': 377.4757000207901,
    'VACSDRED1996_2000': 61396.704392164946,
    'VAHADRED1996_2000': 23913.480358481407,
    'VAHAGFPL1996_2000': 14510.98007184267,
    'VANNDRED1996_2000': 101682.98576274514,
    'VANNGFPL1996_2000': 19003.12744230032,
    'VAVBGILL1996_2000': 1155.9579862952232,
    'VAVBOPTR1996_2000': 829.1799215078354,
    'CTSTDRED2001_2005': 23787.879055708647,
    'CTSTGFMI2001_2005': 1198.7738472521305,
    'CTSTGFPL2001_2005': 3624.021617382765,
    'MABSGFMI2001_2005': 184.18382768332958,
    'MABSGFPL2001_2005': 23207.65614119172,
    'MABSLOBS2001_2005': 364.06197878718376,
    'MABVLOBS2001_2005': 9706.402966082096,
    'MACHGFMI2001_2005': 1801.5818238258362,
    'MACHGILL2001_2005': 20767.15655231476,
    'MACHLOBS2001_2005': 4478.023844003677,
    'MACHLONG2001_2005': 3900.9479534626007,
    'MADNLOBS2001_2005': 323.7008034288883,
    'MAFHDRED2001_2005': 5397.001751512289,
    'MAFHLOBS2001_2005': 6887.8744278252125,
    'MAGLDRED2001_2005': 3213.7874540686607,
    'MAGLGFMI2001_2005': 26935.394461244345,
    'MAGLGFPL2001_2005': 35367.3341242969,
    'MAGLGILL2001_2005': 39504.13610327244,
    'MAGLLOBS2001_2005': 36804.74826619029,
    'MAGLLONG2001_2005': 2589.390779942274,
    'MAGLMSEI2001_2005': 8424.107002526522,
    'MAGLOPTR2001_2005': 6151.358474373817,
    'MAHLLOBS2001_2005': 2123.0157636404037,
    'MAHPLOBS2001_2005': 122.79044750332832,
    'MAHPOPTR2001_2005': 127.75493595004082,
    'MAMFLOBS2001_2005': 5713.7843599021435,
    'MAMHLOBS2001_2005': 3129.443493783474,
    'MANBDRED2001_2005': 398152.8731761277,
    'MANBGFMI2001_2005': 7630.058560401201,
    'MANBGFPL2001_2005': 175539.26194229722,
    'MANBGILL2001_2005': 5724.925546556711,
    'MANBLOBS2001_2005': 8387.27751043439,
    'MANBMSEI2001_2005': 4792.683895498514,
    'MANPDRED2001_2005': 768.7884511649609,
    'MAPLLOBS2001_2005': 3312.9557196497917,
    'MAPTDRED2001_2005': 8051.356370538473,
    'MAPTGFMI2001_2005': 8138.841800838709,
    'MAPTLOBS2001_2005': 586.6407944560051,
    'MARPLOBS2001_2005': 8330.960489362478,
    'MASCGFMI2001_2005': 2820.240237981081,
    'MASCGILL2001_2005': 7404.561825156212,
    'MASCLOBS2001_2005': 5559.072324901819,
    'MASWLOBS2001_2005': 17551.21388116479,
    'MAWPGILL2001_2005': 1916.3680994808674,
    'MAWPLOBS2001_2005': 7367.0233599841595,
    'MDOCGFMI2001_2005': 3058.56621325016,
    'MDOCGILL2001_2005': 901.5968244075775,
    'MDOCLOBS2001_2005': 599.3894208669662,
    'MDOCOPTR2001_2005': 2452.698105722666,
    'MEBALOBS2001_2005': 852.3261616528034,
    'MEBELOBS2001_2005': 2315.828035891056,
    'MEBOLOBS2001_2005': 3350.7996161282063,
    'MECHLOBS2001_2005': 1609.1534936130047,
    'MECPLOBS2001_2005': 2568.0697395801544,
    'MEHWLOBS2001_2005': 2333.851100087166,
    'MEJPDRED2001_2005': 583.1076011359692,
    'MEJPLOBS2001_2005': 5030.543976396322,
    'MEKBLOBS2001_2005': 0,
    'MEKTLOBS2001_2005': 3291.624644547701,
    'MEPODRED2001_2005': 0,
    'MEPOGFMI2001_2005': 25129.799017697573,
    'MEPOGFPL2001_2005': 33254.82529127598,
    'MEPOGILL2001_2005': 14229.818893969059,
    'MEPOLOBS2001_2005': 3585.514249354601,
    'MEPOMSEI2001_2005': 5005.9829755574465,
    'MERKMSEI2001_2005': 1780.5402822941542,
    'MESBLOBS2001_2005': 643.4745595753193,
    'MESGLOBS2001_2005': 6166.967840284109,
    'MESHLOBS2001_2005': 1738.3860425949097,
    'METHLOBS2001_2005': 1400.2280143797398,
    'MEVHLOBS2001_2005': 2314.6200470924377,
    'MEWHLOBS2001_2005': 541.5215453207493,
    'MEYHLOBS2001_2005': 139.65477082133293,
    'NCBEGFPL2001_2005': 5537.077338576317,
    'NCBESHRM2001_2005': 438.39588817954063,
    'NCENGFPL2001_2005': 2228.403431355953,
    'NCENGILL2001_2005': 619.3142767548561,
    'NCENSHRM2001_2005': 58.18836599588394,
    'NCHAGILL2001_2005': 784.7262116074562,
    'NCORGFPL2001_2005': 1879.005590736866,
    'NCORSHRM2001_2005': 494.8618725836277,
    'NCWCGFPL2001_2005': 6960.543984562159,
    'NCWCGILL2001_2005': 2968.2369491755962,
    'NCWCSHRM2001_2005': 385.8467982709408,
    'NHNWLOBS2001_2005': 21769.267985492945,
    'NHPMGFMI2001_2005': 1679.9052372425795,
    'NHPMGILL2001_2005': 13105.835906237364,
    'NHPMLOBS2001_2005': 11341.893239706755,
    'NHRYGILL2001_2005': 3803.839805752039,
    'NHSBGFMI2001_2005': 5915.521010488272,
    'NHSBLOBS2001_2005': 548.5873450636864,
    'NJATOPTR2001_2005': 603.9074662029743,
    'NJBFDRED2001_2005': 246.4836963713169,
    'NJBFGFMI2001_2005': 8514.18534874916,
    'NJBFMSEI2001_2005': 38.526060074567795,
    'NJBGDRED2001_2005': 1327.459456950426,
    'NJBGGILL2001_2005': 1201.179647475481,
    'NJCMDRED2001_2005': 98219.6953047812,
    'NJCMGFPL2001_2005': 28408.356422007084,
    'NJCMGILL2001_2005': 0,
    'NJCMMSEI2001_2005': 3655.7221145778894,
    'NJCMOPTR2001_2005': 1289.5472364127636,
    'NJPPDRED2001_2005': 22610.74923235178,
    'NJPPGFMI2001_2005': 3725.896801710129,
    'NJPPGFPL2001_2005': 8529.297636032104,
    'NJPPGILL2001_2005': 4404.5670484900475,
    'NJPPLOBS2001_2005': 5086.020348250866,
    'NJPPOPTR2001_2005': 144.72840920090675,
    'NJSCLOBS2001_2005': 1119.270506799221,
    'NJSCOPTR2001_2005': 1349.155804514885,
    'NJSRLOBS2001_2005': 859.3805305659771,
    'NJWWDRED2001_2005': 7022.695230990648,
    'NYFRGFMI2001_2005': 1490.8589828014374,
    'NYFRGILL2001_2005': 227.67699363827705,
    'NYMOGFMI2001_2005': 12224.13370013237,
    'NYMOGFPL2001_2005': 6180.255430608988,
    'NYMOGILL2001_2005': 1482.178786188364,
    'NYMOLOBS2001_2005': 3617.0605026483536,
    'NYMOOPTR2001_2005': 312.49309879541397,
    'NYPLGFPL2001_2005': 1927.9540405273438,
    'NYSCGFMI2001_2005': 16883.71903026104,
    'NYSCGILL2001_2005': 2296.738787472248,
    'RILCGILL2001_2005': 4012.5020186901093,
    'RILCLOBS2001_2005': 1644.2828715443611,
    'RILCOPTR2001_2005': 2750.6878620684147,
    'RINPGFMI2001_2005': 807.2807652950287,
    'RINPGILL2001_2005': 2243.8864872455597,
    'RINPLOBS2001_2005': 14181.763380169868,
    'RIPJDRED2001_2005': 3996.8406127393246,
    'RIPJGFMI2001_2005': 13862.655028432608,
    'RIPJGFPL2001_2005': 60235.48236989975,
    'RIPJGILL2001_2005': 4265.886607378721,
    'RIPJLOBS2001_2005': 31161.236877828836,
    'RIPJOPTR2001_2005': 1473.384496241808,
    'RITIGILL2001_2005': 1666.5862609148026,
    'VACHGFMI2001_2005': 7736.454004257917,
    'VACHGFPL2001_2005': 8978.22758808732,
    'VACHGILL2001_2005': 3215.33250823617,
    'VACHOPTR2001_2005': 972.7377895712852,
    'VACSDRED2001_2005': 61919.395459890366,
    'VAHADRED2001_2005': 42640.82423555851,
    'VAHAGFPL2001_2005': 16154.539671123028,
    'VANNDRED2001_2005': 131501.68725067377,
    'VANNGFPL2001_2005': 13734.23136484623,
    'VAVBGILL2001_2005': 1419.30157199502,
    'VAVBOPTR2001_2005': 2835.107985317707,
    'CTSTDRED2006_2010': 11322.946306824684,
    'CTSTGFMI2006_2010': 807.132486552,
    'CTSTGFPL2006_2010': 2197.422506392002,
    'MABSGFMI2006_2010': 997.3241757303476,
    'MABSGFPL2006_2010': 29149.91714257002,
    'MABSLOBS2006_2010': 0,
    'MABVLOBS2006_2010': 7968.294326484203,
    'MACHGFMI2006_2010': 61.83695702254772,
    'MACHGILL2006_2010': 22066.203576683998,
    'MACHLOBS2006_2010': 5703.820277035236,
    'MACHLONG2006_2010': 2256.261950522661,
    'MADNLOBS2006_2010': 295.0481332242489,
    'MAFHDRED2006_2010': 14302.942819982767,
    'MAFHLOBS2006_2010': 219.7502728998661,
    'MAGLDRED2006_2010': 1335.5083883404732,
    'MAGLGFMI2006_2010': 24022.337114095688,
    'MAGLGFPL2006_2010': 40984.34425508976,
    'MAGLGILL2006_2010': 51950.36674079299,
    'MAGLLOBS2006_2010': 31569.474447757006,
    'MAGLLONG2006_2010': 4964.801446586847,
    'MAGLMSEI2006_2010': 10290.931095078588,
    'MAGLOPTR2006_2010': 3062.130106151104,
    'MAHLLOBS2006_2010': 2743.1152475476265,
    'MAHPLOBS2006_2010': 573.7135361731052,
    'MAHPOPTR2006_2010': 1592.9237667024136,
    'MAMFLOBS2006_2010': 5849.259384155273,
    'MAMHLOBS2006_2010': 2503.4594803750515,
    'MANBDRED2006_2010': 384356.1166482866,
    'MANBGFMI2006_2010': 1237.3975711166859,
    'MANBGFPL2006_2010': 129346.28731238842,
    'MANBGILL2006_2010': 6209.1853758990765,
    'MANBLOBS2006_2010': 21844.315599679947,
    'MANBMSEI2006_2010': 6232.884969338775,
    'MANPDRED2006_2010': 318.7543907761574,
    'MAPLLOBS2006_2010': 707.7670770287514,
    'MAPTDRED2006_2010': 4514.2940723598,
    'MAPTGFMI2006_2010': 4314.798670291901,
    'MAPTLOBS2006_2010': 592.7428289651871,
    'MARPLOBS2006_2010': 7697.142448067665,
    'MASCGFMI2006_2010': 3205.427978515625,
    'MASCGILL2006_2010': 8009.014892756939,
    'MASCLOBS2006_2010': 4778.823698788881,
    'MASWLOBS2006_2010': 11516.870030283928,
    'MAWPGILL2006_2010': 1153.721094995737,
    'MAWPLOBS2006_2010': 4794.934579342604,
    'MDOCGFMI2006_2010': 2498.5078016519547,
    'MDOCGILL2006_2010': 1387.0102048814297,
    'MDOCLOBS2006_2010': 643.9523886740208,
    'MDOCOPTR2006_2010': 2182.9099717736244,
    'MEBALOBS2006_2010': 1345.2108205854893,
    'MEBELOBS2006_2010': 1390.4226531982422,
    'MEBOLOBS2006_2010': 1968.3589795827866,
    'MECHLOBS2006_2010': 1551.3562146127224,
    'MECPLOBS2006_2010': 2236.6550607979298,
    'MEHWLOBS2006_2010': 2767.4883269667625,
    'MEJPDRED2006_2010': 33.095338970422745,
    'MEJPLOBS2006_2010': 5867.152765065432,
    'MEKBLOBS2006_2010': 236.1224848330021,
    'MEKTLOBS2006_2010': 2441.9847811460495,
    'MEPODRED2006_2010': 0,
    'MEPOGFMI2006_2010': 7203.338215321302,
    'MEPOGFPL2006_2010': 8672.710652679205,
    'MEPOGILL2006_2010': 9922.650258123875,
    'MEPOLOBS2006_2010': 1784.7839006483555,
    'MEPOMSEI2006_2010': 1186.1558247953653,
    'MERKMSEI2006_2010': 1619.0297332406044,
    'MESBLOBS2006_2010': 167.0884221792221,
    'MESGLOBS2006_2010': 3436.4477337300777,
    'MESHLOBS2006_2010': 856.93247833848,
    'METHLOBS2006_2010': 1414.365039497614,
    'MEVHLOBS2006_2010': 2675.7034626305103,
    'MEWHLOBS2006_2010': 1929.1090382039547,
    'MEYHLOBS2006_2010': 953.0928908288479,
    'NCBEGFPL2006_2010': 3564.3334408700466,
    'NCBESHRM2006_2010': 38.498228937387466,
    'NCENGFPL2006_2010': 1403.4684245735407,
    'NCENGILL2006_2010': 168.74756979942322,
    'NCENSHRM2006_2010': 113.1024860739708,
    'NCHAGILL2006_2010': 282.6306172311306,
    'NCORGFPL2006_2010': 2508.4617696404457,
    'NCORSHRM2006_2010': 70.78304213285446,
    'NCWCGFPL2006_2010': 7423.569225162268,
    'NCWCGILL2006_2010': 3116.8105108439922,
    'NCWCSHRM2006_2010': 86.11198794841766,
    'NHNWLOBS2006_2010': 29284.844132572412,
    'NHPMGFMI2006_2010': 1028.883861064911,
    'NHPMGILL2006_2010': 7589.453544914722,
    'NHPMLOBS2006_2010': 8187.760240852833,
    'NHRYGILL2006_2010': 5368.1015567183495,
    'NHSBGFMI2006_2010': 2707.9248984754086,
    'NHSBLOBS2006_2010': 1535.7880661189556,
    'NJATOPTR2006_2010': 932.668586730957,
    'NJBFDRED2006_2010': 191.81473258137703,
    'NJBFGFMI2006_2010': 7091.569567590952,
    'NJBFMSEI2006_2010': 48.651256799697876,
    'NJBGDRED2006_2010': 19804.094205468893,
    'NJBGGILL2006_2010': 5706.004608362913,
    'NJCMDRED2006_2010': 111273.93171527982,
    'NJCMGFPL2006_2010': 13466.254673540592,
    'NJCMGILL2006_2010': 0,
    'NJCMMSEI2006_2010': 3979.1182765364647,
    'NJCMOPTR2006_2010': 1433.7001388669014,
    'NJPPDRED2006_2010': 36195.17001065612,
    'NJPPGFMI2006_2010': 3220.8220392763615,
    'NJPPGFPL2006_2010': 11819.248063027859,
    'NJPPGILL2006_2010': 2661.6038938462734,
    'NJPPLOBS2006_2010': 6191.0703311264515,
    'NJPPOPTR2006_2010': 377.1265140771866,
    'NJSCLOBS2006_2010': 871.3821714520454,
    'NJSCOPTR2006_2010': 1899.8919558823109,
    'NJSRLOBS2006_2010': 1667.6023027300835,
    'NJWWDRED2006_2010': 4867.698594659567,
    'NYFRGFMI2006_2010': 1124.567346572876,
    'NYFRGILL2006_2010': 239.1819028556347,
    'NYMOGFMI2006_2010': 10740.74860727787,
    'NYMOGFPL2006_2010': 9368.881295204163,
    'NYMOGILL2006_2010': 2955.7315813302994,
    'NYMOLOBS2006_2010': 3201.599818557501,
    'NYMOOPTR2006_2010': 334.48313504457474,
    'NYPLGFPL2006_2010': 7471.14534959197,
    'NYSCGFMI2006_2010': 10521.220524519682,
    'NYSCGILL2006_2010': 2038.1022353768349,
    'RILCGILL2006_2010': 5216.176013946533,
    'RILCLOBS2006_2010': 2101.2008271813393,
    'RILCOPTR2006_2010': 3079.168932288885,
    'RINPGFMI2006_2010': 283.7207183390856,
    'RINPGILL2006_2010': 2368.601499348879,
    'RINPLOBS2006_2010': 16058.132380902767,
    'RIPJDRED2006_2010': 5348.636597648263,
    'RIPJGFMI2006_2010': 17458.777430057526,
    'RIPJGFPL2006_2010': 51801.801310032606,
    'RIPJGILL2006_2010': 3783.6629602611065,
    'RIPJLOBS2006_2010': 31381.93842113018,
    'RIPJOPTR2006_2010': 2481.125846117735,
    'RITIGILL2006_2010': 1022.9480813443661,
    'VACHGFMI2006_2010': 3673.7476107776165,
    'VACHGFPL2006_2010': 6252.770279705524,
    'VACHGILL2006_2010': 2470.2457196712494,
    'VACHOPTR2006_2010': 526.5730432868004,
    'VACSDRED2006_2010': 40475.73625686765,
    'VAHADRED2006_2010': 27795.89765560627,
    'VAHAGFPL2006_2010': 2268.4684276878834,
    'VANNDRED2006_2010': 81611.57191973925,
    'VANNGFPL2006_2010': 1435.722090601921,
    'VAVBGILL2006_2010': 683.1004108190536,
    'VAVBOPTR2006_2010': 3138.0827838480473,
    'CTSTDRED2011_2015': 6067.638066768646,
    'CTSTGFMI2011_2015': 341.14016845822334,
    'CTSTGFPL2011_2015': 171.27930426597595,
    'MABSGFMI2011_2015': 9982.631202667952,
    'MABSGFPL2011_2015': 25081.217992842197,
    'MABSLOBS2011_2015': 1108.1836791038513,
    'MABVLOBS2011_2015': 8782.875593185425,
    'MACHGFMI2011_2015': 112.99516132473946,
    'MACHGILL2011_2015': 21768.792266905308,
    'MACHLOBS2011_2015': 5951.685560703278,
    'MACHLONG2011_2015': 1913.4273162782192,
    'MADNLOBS2011_2015': 304.8524321913719,
    'MAFHDRED2011_2015': 9465.423324525356,
    'MAFHLOBS2011_2015': 820.0281520485878,
    'MAGLDRED2011_2015': 1825.971167176962,
    'MAGLGFMI2011_2015': 13813.906457558274,
    'MAGLGFPL2011_2015': 45504.9508228302,
    'MAGLGILL2011_2015': 23024.651685357094,
    'MAGLLOBS2011_2015': 35441.64710494876,
    'MAGLLONG2011_2015': 1441.1410910487175,
    'MAGLMSEI2011_2015': 7430.7499343156815,
    'MAGLOPTR2011_2015': 515.2623766362667,
    'MAHLLOBS2011_2015': 2407.467100352049,
    'MAHPLOBS2011_2015': 65.05787083506584,
    'MAHPOPTR2011_2015': 1962.349578231573,
    'MAMFLOBS2011_2015': 6448.332591205835,
    'MAMHLOBS2011_2015': 1577.704179674387,
    'MANBDRED2011_2015': 319575.6076710522,
    'MANBGFMI2011_2015': 705.227853000164,
    'MANBGFPL2011_2015': 114462.40228688717,
    'MANBGILL2011_2015': 5309.573096245527,
    'MANBLOBS2011_2015': 24452.540201693773,
    'MANBMSEI2011_2015': 949.184799015522,
    'MANPDRED2011_2015': 297.1146710515022,
    'MAPLLOBS2011_2015': 2030.2787985503674,
    'MAPTDRED2011_2015': 9168.088457792997,
    'MAPTGFMI2011_2015': 670.0257593095303,
    'MAPTLOBS2011_2015': 2179.7175610661507,
    'MARPLOBS2011_2015': 7479.950001388788,
    'MASCGFMI2011_2015': 1883.9688557982445,
    'MASCGILL2011_2015': 5612.024584963918,
    'MASCLOBS2011_2015': 3889.7471943199635,
    'MASWLOBS2011_2015': 10937.37191748619,
    'MAWPGILL2011_2015': 1695.4311213195324,
    'MAWPLOBS2011_2015': 2864.055249273777,
    'MDOCGFMI2011_2015': 1755.7542291283607,
    'MDOCGILL2011_2015': 2789.3506393432617,
    'MDOCLOBS2011_2015': 1230.858835875988,
    'MDOCOPTR2011_2015': 1022.1368620693684,
    'MEBALOBS2011_2015': 1173.3849091231823,
    'MEBELOBS2011_2015': 966.3180363178253,
    'MEBOLOBS2011_2015': 1607.8073945343494,
    'MECHLOBS2011_2015': 2935.6473173499107,
    'MECPLOBS2011_2015': 2453.4805846512318,
    'MEHWLOBS2011_2015': 2896.5647817254066,
    'MEJPDRED2011_2015': 85.68997806310654,
    'MEJPLOBS2011_2015': 5397.698349475861,
    'MEKBLOBS2011_2015': 2001.8006504774094,
    'MEKTLOBS2011_2015': 1772.593041330576,
    'MEPODRED2011_2015': 191.45221412181854,
    'MEPOGFMI2011_2015': 4709.500459969044,
    'MEPOGFPL2011_2015': 2192.6762012690306,
    'MEPOGILL2011_2015': 16746.746905088425,
    'MEPOLOBS2011_2015': 926.8166832327843,
    'MEPOMSEI2011_2015': 6121.989266723394,
    'MERKMSEI2011_2015': 3334.137733131647,
    'MESBLOBS2011_2015': 977.7235692441463,
    'MESGLOBS2011_2015': 1394.5251497328281,
    'MESHLOBS2011_2015': 154.06147007644176,
    'METHLOBS2011_2015': 2466.1627181768417,
    'MEVHLOBS2011_2015': 6595.981788665056,
    'MEWHLOBS2011_2015': 2524.5555127561092,
    'MEYHLOBS2011_2015': 1800.5319995582104,
    'NCBEGFPL2011_2015': 892.7461373209953,
    'NCBESHRM2011_2015': 202.66752606630325,
    'NCENGFPL2011_2015': 690.6567880064249,
    'NCENGILL2011_2015': 378.3185090124607,
    'NCENSHRM2011_2015': 231.2487317621708,
    'NCHAGILL2011_2015': 1042.1386302411556,
    'NCORGFPL2011_2015': 606.6526936292648,
    'NCORSHRM2011_2015': 9.012310981750488,
    'NCWCGFPL2011_2015': 1447.3411051630974,
    'NCWCGILL2011_2015': 3007.4904182851315,
    'NCWCSHRM2011_2015': 106.1453517973423,
    'NHNWLOBS2011_2015': 45068.314921438694,
    'NHPMGFMI2011_2015': 260.4215714931488,
    'NHPMGILL2011_2015': 5034.017490565777,
    'NHPMLOBS2011_2015': 7924.155635982752,
    'NHRYGILL2011_2015': 5109.794679254293,
    'NHSBGFMI2011_2015': 2987.1769047677517,
    'NHSBLOBS2011_2015': 1345.3123927414417,
    'NJATOPTR2011_2015': 822.8768430948257,
    'NJBFDRED2011_2015': 109.19673481583595,
    'NJBFGFMI2011_2015': 5336.84523910284,
    'NJBFMSEI2011_2015': 56.13199710845947,
    'NJBGDRED2011_2015': 27977.930989801884,
    'NJBGGILL2011_2015': 10556.175066500902,
    'NJCMDRED2011_2015': 59748.45638728142,
    'NJCMGFPL2011_2015': 13600.231845468283,
    'NJCMGILL2011_2015': 0,
    'NJCMMSEI2011_2015': 4062.7049196362495,
    'NJCMOPTR2011_2015': 1040.7018673717976,
    'NJPPDRED2011_2015': 25552.740704476833,
    'NJPPGFMI2011_2015': 3385.5100647211075,
    'NJPPGFPL2011_2015': 9003.625827103853,
    'NJPPGILL2011_2015': 4855.858980625868,
    'NJPPLOBS2011_2015': 5709.7768069803715,
    'NJPPOPTR2011_2015': 290.15666139125824,
    'NJSCLOBS2011_2015': 1049.641810566187,
    'NJSCOPTR2011_2015': 1434.3692426383495,
    'NJSRLOBS2011_2015': 344.4050081372261,
    'NJWWDRED2011_2015': 5137.498624414206,
    'NYFRGFMI2011_2015': 526.7516205012798,
    'NYFRGILL2011_2015': 134.7256242930889,
    'NYMOGFMI2011_2015': 12047.177850306034,
    'NYMOGFPL2011_2015': 6376.905061691999,
    'NYMOGILL2011_2015': 3580.8610469400883,
    'NYMOLOBS2011_2015': 2322.638678610325,
    'NYMOOPTR2011_2015': 891.4888302087784,
    'NYPLGFPL2011_2015': 1856.4691179692745,
    'NYSCGFMI2011_2015': 6877.837803810835,
    'NYSCGILL2011_2015': 1688.9021866321564,
    'RILCGILL2011_2015': 5782.611346423626,
    'RILCLOBS2011_2015': 1868.8890634179115,
    'RILCOPTR2011_2015': 1186.911946117878,
    'RINPGFMI2011_2015': 161.47325929999352,
    'RINPGILL2011_2015': 2942.271966904402,
    'RINPLOBS2011_2015': 12466.438345193863,
    'RIPJDRED2011_2015': 5047.361510887742,
    'RIPJGFMI2011_2015': 23088.28397411108,
    'RIPJGFPL2011_2015': 42520.22210666537,
    'RIPJGILL2011_2015': 4842.580439180136,
    'RIPJLOBS2011_2015': 21150.53848040104,
    'RIPJOPTR2011_2015': 1151.8022025227547,
    'RITIGILL2011_2015': 1151.0421995222569,
    'VACHGFMI2011_2015': 272.0125108361244,
    'VACHGFPL2011_2015': 1491.2900395095348,
    'VACHGILL2011_2015': 2491.5119191408157,
    'VACHOPTR2011_2015': 805.7478822767735,
    'VACSDRED2011_2015': 29825.849527090788,
    'VAHADRED2011_2015': 9164.224975526333,
    'VAHAGFPL2011_2015': 4949.753610670567,
    'VANNDRED2011_2015': 36335.25505340099,
    'VANNGFPL2011_2015': 5145.466717094183,
    'VAVBGILL2011_2015': 2575.5831636488438,
    'VAVBOPTR2011_2015': 1621.9740790128708,
    'CTSTDRED2016_2020': 5777.244718760252,
    'CTSTGFMI2016_2020': 1677.6122033894062,
    'CTSTGFPL2016_2020': 474.67293068766594,
    'MABSGFMI2016_2020': 7474.767123162746,
    'MABSGFPL2016_2020': 19059.261420339346,
    'MABSLOBS2016_2020': 716.2011273503304,
    'MABVLOBS2016_2020': 7131.5605390667915,
    'MACHGFMI2016_2020': 122.808733060956,
    'MACHGILL2016_2020': 20714.530285656452,
    'MACHLOBS2016_2020': 4918.060510247946,
    'MACHLONG2016_2020': 2439.0949217379093,
    'MADNLOBS2016_2020': 40.78635096549988,
    'MAFHDRED2016_2020': 1569.877668440342,
    'MAFHLOBS2016_2020': 267.14164528250694,
    'MAGLDRED2016_2020': 2017.6027745008469,
    'MAGLGFMI2016_2020': 12336.315113902092,
    'MAGLGFPL2016_2020': 33633.084973573685,
    'MAGLGILL2016_2020': 4830.049864709377,
    'MAGLLOBS2016_2020': 31203.959091335535,
    'MAGLLONG2016_2020': 493.0245462357998,
    'MAGLMSEI2016_2020': 3482.681541055441,
    'MAGLOPTR2016_2020': 414.4098367393017,
    'MAHLLOBS2016_2020': 1433.220785677433,
    'MAHPLOBS2016_2020': 38.87073189020157,
    'MAHPOPTR2016_2020': 1823.630754083395,
    'MAMFLOBS2016_2020': 5900.255000501871,
    'MAMHLOBS2016_2020': 1346.7105207443237,
    'MANBDRED2016_2020': 260900.88815903664,
    'MANBGFMI2016_2020': 763.1146849095821,
    'MANBGFPL2016_2020': 53764.95732575655,
    'MANBGILL2016_2020': 1067.582116946578,
    'MANBLOBS2016_2020': 29695.249012112617,
    'MANBMSEI2016_2020': 269.38442324101925,
    'MANPDRED2016_2020': 169.2786139845848,
    'MAPLLOBS2016_2020': 1953.3492658436298,
    'MAPTDRED2016_2020': 3335.109444975853,
    'MAPTGFMI2016_2020': 99.23477509617805,
    'MAPTLOBS2016_2020': 1870.5976371467113,
    'MARPLOBS2016_2020': 8911.168294489384,
    'MASCGFMI2016_2020': 1730.893249064684,
    'MASCGILL2016_2020': 2519.4115503132343,
    'MASCLOBS2016_2020': 3617.52579036355,
    'MASWLOBS2016_2020': 9004.767720550299,
    'MAWPGILL2016_2020': 161.20090422034264,
    'MAWPLOBS2016_2020': 981.836140036583,
    'MDOCGFMI2016_2020': 951.4082061350346,
    'MDOCGILL2016_2020': 1561.7670456767082,
    'MDOCLOBS2016_2020': 342.7733801007271,
    'MDOCOPTR2016_2020': 1299.5359782874584,
    'MEBALOBS2016_2020': 436.25832810997963,
    'MEBELOBS2016_2020': 596.399994879961,
    'MEBOLOBS2016_2020': 1804.1988295018673,
    'MECHLOBS2016_2020': 3183.059893786907,
    'MECPLOBS2016_2020': 1877.9270796775818,
    'MEHWLOBS2016_2020': 2249.4906035363674,
    'MEJPDRED2016_2020': 88.12165501713753,
    'MEJPLOBS2016_2020': 2450.9135173261166,
    'MEKBLOBS2016_2020': 614.4628546535969,
    'MEKTLOBS2016_2020': 1477.6011865735054,
    'MEPODRED2016_2020': 63.23091700673103,
    'MEPOGFMI2016_2020': 2770.941731929779,
    'MEPOGFPL2016_2020': 140.23929496109486,
    'MEPOGILL2016_2020': 6747.348320513964,
    'MEPOLOBS2016_2020': 1598.9138872921467,
    'MEPOMSEI2016_2020': 3157.9100255072117,
    'MERKMSEI2016_2020': 2714.831251874566,
    'MESBLOBS2016_2020': 1230.8744454085827,
    'MESGLOBS2016_2020': 930.3164661824703,
    'MESHLOBS2016_2020': 2025.1130681037903,
    'METHLOBS2016_2020': 2276.6659292578697,
    'MEVHLOBS2016_2020': 2602.218360453844,
    'MEWHLOBS2016_2020': 1852.711230903864,
    'MEYHLOBS2016_2020': 264.022696942091,
    'NCBEGFPL2016_2020': 373.8941329270601,
    'NCBESHRM2016_2020': 355.00554341077805,
    'NCENGFPL2016_2020': 640.0369436144829,
    'NCENGILL2016_2020': 0,
    'NCENSHRM2016_2020': 458.54701310396194,
    'NCHAGILL2016_2020': 27.05635303258896,
    'NCORGFPL2016_2020': 86.280577942729,
    'NCORSHRM2016_2020': 21.993994176387787,
    'NCWCGFPL2016_2020': 286.42173857986927,
    'NCWCGILL2016_2020': 2919.5202700793743,
    'NCWCSHRM2016_2020': 89.40303120017052,
    'NHNWLOBS2016_2020': 41202.36227661371,
    'NHPMGFMI2016_2020': 0,
    'NHPMGILL2016_2020': 1016.0381708443165,
    'NHPMLOBS2016_2020': 6356.939038515091,
    'NHRYGILL2016_2020': 896.7446363270283,
    'NHSBGFMI2016_2020': 1575.3665371537209,
    'NHSBLOBS2016_2020': 1649.1453315019608,
    'NJATOPTR2016_2020': 244.16491243243217,
    'NJBFDRED2016_2020': 24.9640411734581,
    'NJBFGFMI2016_2020': 3490.4675856232643,
    'NJBFMSEI2016_2020': 17.24547901749611,
    'NJBGDRED2016_2020': 10608.334724783897,
    'NJBGGILL2016_2020': 6575.1165717840195,
    'NJCMDRED2016_2020': 38098.38974425197,
    'NJCMGFPL2016_2020': 5509.699174940586,
    'NJCMGILL2016_2020': 0,
    'NJCMMSEI2016_2020': 2876.1178399920464,
    'NJCMOPTR2016_2020': 2325.4076306819916,
    'NJPPDRED2016_2020': 16969.78822055459,
    'NJPPGFMI2016_2020': 1857.2082661092281,
    'NJPPGFPL2016_2020': 7985.973577290773,
    'NJPPGILL2016_2020': 3287.5223040878773,
    'NJPPLOBS2016_2020': 4200.683652371168,
    'NJPPOPTR2016_2020': 147.22504124045372,
    'NJSCLOBS2016_2020': 1114.308038532734,
    'NJSCOPTR2016_2020': 1435.7413685917854,
    'NJSRLOBS2016_2020': 179.97480008006096,
    'NJWWDRED2016_2020': 4713.306292027235,
    'NYFRGFMI2016_2020': 99.15797200798988,
    'NYFRGILL2016_2020': 99.58213794231415,
    'NYMOGFMI2016_2020': 8321.329236984253,
    'NYMOGFPL2016_2020': 4473.859006896615,
    'NYMOGILL2016_2020': 1894.8364154696465,
    'NYMOLOBS2016_2020': 1470.9450264573097,
    'NYMOOPTR2016_2020': 217.20727902650833,
    'NYPLGFPL2016_2020': 366.97377160191536,
    'NYSCGFMI2016_2020': 2861.167854577303,
    'NYSCGILL2016_2020': 1248.2920863628387,
    'RILCGILL2016_2020': 3688.3452863395214,
    'RILCLOBS2016_2020': 1613.7075340747833,
    'RILCOPTR2016_2020': 984.4659560620785,
    'RINPGFMI2016_2020': 167.98812714219093,
    'RINPGILL2016_2020': 1913.949491918087,
    'RINPLOBS2016_2020': 10811.789149612188,
    'RIPJDRED2016_2020': 5724.3956552147865,
    'RIPJGFMI2016_2020': 20789.56211811304,
    'RIPJGFPL2016_2020': 38792.663910895586,
    'RIPJGILL2016_2020': 4044.3608860969543,
    'RIPJLOBS2016_2020': 11645.262773871422,
    'RIPJOPTR2016_2020': 1404.6478580832481,
    'RITIGILL2016_2020': 710.6368026733398,
    'VACHGFMI2016_2020': 0.2538810074329376,
    'VACHGFPL2016_2020': 0,
    'VACHGILL2016_2020': 1486.0770618021488,
    'VACHOPTR2016_2020': 483.5884169638157,
    'VACSDRED2016_2020': 10666.33841651678,
    'VAHADRED2016_2020': 10857.787679940462,
    'VAHAGFPL2016_2020': 2091.5978857576847,
    'VANNDRED2016_2020': 10650.107449680567,
    'VANNGFPL2016_2020': 614.3954587578773,
    'VAVBGILL2016_2020': 3350.1123812794685,
    'VAVBOPTR2016_2020': 62.83737191557884
};

const communityMapThresholds = {
    'CTSTDRED': 0.537995338,
    'CTSTGFMI': 0.769206062,
    'CTSTGFPL': 0.352010526,
    'MABSGFMI': 0.541208595,
    'MABSGFPL': 0.854639009,
    'MABSLOBS': 1.043430626,
    'MABVLOBS': 4.833441496,
    'MACHGILL': 1.386351526,
    'MACHGFMI': 0.922363684,
    'MACHLOBS': 2.080784559,
    'MACHLONG': 0.787499219,
    'MADNLOBS': 0.751884818,
    'MAFHDRED': 0.391293794,
    'MAFHLOBS': 0.728066504,
    'MAGLDRED': 0.564944714,
    'MAGLGILL': 0.967642546,
    'MAGLGFMI': 0.713412762,
    'MAGLGFPL': 0.978277266,
    'MAGLLOBS': 2.735666871,
    'MAGLLONG': 0.683139622,
    'MAGLOPTR': 0.405814677,
    'MAGLMSEI': 0.502356693,
    'MAHLLOBS': 2.107169688,
    'MAHPLOBS': 0.468161449,
    'MAHPOPTR': 1.297538638,
    'MAMFLOBS': 3.084862411,
    'MAMHLOBS': 2.585172057,
    'MANBDRED': 3.574389458,
    'MANBGILL': 0.447264567,
    'MANBGFMI': 0.531030893,
    'MANBGFPL': 1.643927276,
    'MANBLOBS': 0.989926338,
    'MANBMSEI': 0.304024607,
    'MANPDRED': 0.560530797,
    'MAPLLOBS': 1.949747086,
    'MAPTDRED': 1.155244648,
    'MAPTGFMI': 2.279961467,
    'MAPTLOBS': 1.085656643,
    'MARPLOBS': 6.238860369,
    'MASCGILL': 1.892787099,
    'MASCGFMI': 1.384013414,
    'MASCLOBS': 2.529951513,
    'MASWLOBS': 1.041792393,
    'MAWPGILL': 0.444871828,
    'MAWPLOBS': 0.589103699,
    'MDOCGILL': 0.633620441,
    'MDOCGFMI': 1.302592576,
    'MDOCLOBS': 0.366819866,
    'MDOCOPTR': 0.658440322,
    'MEBALOBS': 1.72990346,
    'MEBELOBS': 2.179673791,
    'MEBOLOBS': 2.005787432,
    'MECHLOBS': 1.93266803,
    'MECPLOBS': 3.036244631,
    'MEHWLOBS': 2.493258476,
    'MEJPDRED': 0.684433877,
    'MEJPLOBS': 3.709727645,
    'MEKBLOBS': 1.199265838,
    'MEKTLOBS': 2.951433897,
    'MEPODRED': 0.436649427,
    'MEPOGILL': 0.859089434,
    'MEPOGFMI': 0.803764641,
    'MEPOGFPL': 0.772028744,
    'MEPOLOBS': 1.947585464,
    'MEPOMSEI': 0.513369784,
    'MERKMSEI': 0.410615087,
    'MESBLOBS': 1.018145084,
    'MESGLOBS': 2.08529067,
    'MESHLOBS': 1.603350699,
    'METHLOBS': 2.082540274,
    'MEVHLOBS': 2.109687209,
    'MEWHLOBS': 2.488109767,
    'MEYHLOBS': 1.696028292,
    'NCBEGRPL': 0.307046771,
    'NCBESHRM': 0.725489691,
    'NCENGILL': 0.294709131,
    'NCENGFPL': 0.199641518,
    'NCENSHRM': 1.081609607,
    'NCHAGILL': 0.528095961,
    'NCORGFPL': 0.204240218,
    'NCORSHRM': 0.908714056,
    'NCWCGILL': 0.689839214,
    'NCWCGFPL': 0.321142957,
    'NCWCSHRM': 1.044816077,
    'NHNWLOBS': 1.685296446,
    'NHPMGILL': 0.924938202,
    'NHPMGFMI': 0.534543946,
    'NHPMLOBS': 1.430886745,
    'NHRYGILL': 1.435377896,
    'NHSBGFMI': 1.544564009,
    'NHSBLOBS': 2.246466696,
    'NJATOPTR': 0.878050789,
    'NJBFDRED': 0.993307382,
    'NJBFGRMI': 1.437597364,
    'NJBFMSEI': 1.464561939,
    'NJBGDRED': 0.87071535,
    'NJBGGILL': 0.846834034,
    'NJCMDRED': 1.814181328,
    'NJCMGILL': 0.285726696,
    'NJCMGFPL': 0.92268002,
    'NJCMOPTR': 0.902628362,
    'NJCMMSEI': 0.396447986,
    'NJPPDRED': 1.091861486,
    'NJPPGILL': 1.00536406,
    'NJPPGFMI': 0.60370779,
    'NJPPGFPL': 0.7072317,
    'NJPPLOBS': 1.45276767,
    'NJPPOPTR': 0.319749355,
    'NJSCLOBS': 0.468109392,
    'NJSCOPTR': 0.762414038,
    'NJSRLOBS': 0.754690528,
    'NJWWDRED': 0.524309099,
    'NYFRGILL': 0.680012524,
    'NYFRGFMI': 1.027182698,
    'NYMOGILL': 1.098284364,
    'NYMOGFMI': 0.630327255,
    'NYMOGFPL': 0.37391752,
    'NYMOLOBS': 0.749682337,
    'NYMOOPTR': 0.843667507,
    'NYPLGFPL': 0.438585296,
    'NYSCGILL': 1.353152394,
    'NYSCGFMI': 1.0350959,
    'RILCGILL': 0.910320699,
    'RILCLOBS': 0.811948419,
    'RILCOPTR': 1.821903586,
    'RINPGILL': 0.520173714,
    'RINPGFMI': 0.421350926,
    'RINPLOBS': 1.045031667,
    'RIPJDRED': 0.377486706,
    'RIPJGILL': 0.955858573,
    'RIPJGFMI': 0.727100521,
    'RIPJGFPL': 1.080351353,
    'RIPJLOBS': 1.285351038,
    'RIPJOPTR': 0.913079515,
    'RITIGILL': 0.528698057,
    'VACHGILL': 0.824557722,
    'VACHGFMI': 1.258250892,
    'VACHGFPL': 0.601205081,
    'VACHOPTR': 0.363955677,
    'VACSDRED': 1.232909799,
    'VAHADRED': 0.858626425,
    'VAHAGFPL': 0.36423932,
    'VANNDRED': 1.831099331,
    'VANNGFPL': 0.513265669,
    'VAVBGILL': 1.115654349,
    'VAVBOPTR': 0.587030172
};
const dependencyIntensity = ref(true)

const dependencyColorFunc = (v, attr) =>{ 
    if (communityMapThresholds[attr] == undefined){
        return 'white' // these communities don't exist
    }
    else{
        if (v >= 75) return '#413360' 
        else if (v >= 50 && v < 75) return '#7C66AD'
        else if (v >= 25 && v < 50) return '#DACAFD'
        else if (v > 0 && v < 25) return '#F0E9FF'
        else return '#aeaeae'
    }
}

const labelColorFunc = (v) =>{ 
    if (v >= 75) return 'white' 
    else if (v >= 50 && v < 75) return 'white'
    else if (v >= 25 && v < 50) return 'black'
    else if (v >= 10 && v < 25) return 'black'
    else if (v > 0 && v < 10) return 'black'
    else return 'black'
}

const infoDialogs = ref({
    about:{
        pName: "About these Data",
        model: false,
        text: "Communities at Sea maps were produced using methods developed by Dr. Kevin St. Martin at Rutgers University. The data are based on Vessel Trip Report (VTR) data and vessel permit data."
    },
    dependency:{
        pName: "Fishing Dependency",
        model: false,
        text: "“Dependency” is a measure of how reliant a fleet is of the resources within the area interest. It is the percent of fishing activity that fall inside the area of interest compared to the whole region. “Fisher days” are a measure of labor-time derived from crew size and length of trip."
    },
    intensity:{
        pName: "Fishing Intensity",
        model: false,
        text: "Fishing Intensity is a measure of a fleet's total effort within an area of interest. It is the sum of fishing activity within the area selected. “Fisher days” are a measure of labor-time derived from crew size and length of trip."
    },
    dependencyIntensity: {
        pName: "Fishing Dependency and Intensity",
        model: false,
        text: "Fishing dependency is a measure of the percent of fishing effort for a given community within the Area of Interest. Fishing intensity is a sum of the number of fisher days spent within the AOI."
    }
})

const highlightPort = (col, row) =>{
    const portCode = getKeyByValue(portPrettyNames, row.name)
    selectedPortCode.value = portCode
    applySelectedPortHighlight()

    // store the style attribute used for CAS layer styling so it can be reapplied
    // when the time period changes.
    const gearCode = gearCodesReverse[col.name]
    if (gearCode){
        selectedStyleAttr.value = portCode + gearCode
        selectedLegendText.value = `${col.label}: ${row.name}`
        applySelectedStyle()
    }
}

onMounted(() => {
    if (activeLayerID.value != casLayer.value){
        loadDataByID(casLayer.value, version)
    }
    // console.log(map.value)
    if (!map.value.getLayer('highlighted-port')) {
        map.value.addLayer({
            'id': 'highlighted-port',
            'type': 'circle',
            'source': "composite",
            'source-layer' : "crcs_ne_cas_ports",
            'filter': ['==', 'TOWNCODE', 'xx'],
            'paint': {
                'circle-radius': 10,
                'circle-color': '#1D8177',
                'circle-stroke-width': 2,
                'circle-stroke-color': 'black'
            }
        });
    }
    map.value.setLayoutProperty('highlighted-port', 'visibility', 'visible')

    // If a port was previously selected, re-apply it once the highlight layer exists
    applySelectedPortHighlight()
    applySelectedStyle()
})

watch(selectedPortCode, () => {
    applySelectedPortHighlight()
})

onUnmounted( () =>{
  clearMapStyle()
});

</script>
<style scoped>
.table-container{
    max-width: 400;
    /* overflow: hidden; */

}
.tableheader{
    text-wrap: wrap;
  
}
</style>